import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, User, Loader, UserPlus } from 'lucide-react';
import EnterpriseModal from '../EnterpriseModal';

/**
 * StudentSearchModal - Reusable modal for searching and selecting students
 * Used in dashboard for meeting creation and survey filling
 */
export default function StudentSearchModal({ isOpen, onClose, onSelect, students = [], title, subtitle, showSkipButton = false, onSkip }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Search functionality with debouncing
  useEffect(() => {
    const performSearch = () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const query = searchQuery.toLowerCase();
        const studentArray = Array.isArray(students) ? students : (students?.data || []);

        const results = studentArray.filter(student => {
          const name = `${student.firstName || ''} ${student.lastName || ''}`.toLowerCase();
          const email = (student.email || '').toLowerCase();
          const phone = (student.phone || '').toLowerCase();

          return name.includes(query) || email.includes(query) || phone.includes(query);
        });

        setSearchResults(results.slice(0, 20));
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, students]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen]);

  const handleSelect = (student) => {
    onSelect(student);
    onClose();
  };

  return (
    <EnterpriseModal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      bodyPadding="0.5rem"
      headerContent={
        <div>
          {/* Title row */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <div>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
                margin: 0
              }}>
                {title || t('students.searchStudent')}
              </h2>
              {subtitle && (
                <p style={{
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-secondary)',
                  margin: '0.25rem 0 0 0',
                  fontWeight: '400'
                }}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Search Input - sticky with header */}
          <div style={{ position: 'relative' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: isRTL ? 'auto' : '1rem',
                right: isRTL ? '1rem' : 'auto',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-secondary)'
              }}
            />
            <input
              type="text"
              placeholder={t('students.searchByNameEmailPhone')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                paddingLeft: isRTL ? '1rem' : '3rem',
                paddingRight: isRTL ? '3rem' : '1rem',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '0.9375rem',
                outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                direction: isRTL ? 'rtl' : 'ltr',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--color-primary-dark)';
                e.target.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--color-border)';
                e.target.style.boxShadow = 'none';
              }}
            />
            {isSearching && (
              <Loader
                size={16}
                style={{
                  position: 'absolute',
                  right: isRTL ? 'auto' : '1rem',
                  left: isRTL ? '1rem' : 'auto',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-primary-dark)',
                  animation: 'ssmSpin 1s linear infinite'
                }}
              />
            )}
          </div>
          <style>{`
            @keyframes ssmSpin {
              from { transform: translateY(-50%) rotate(0deg); }
              to { transform: translateY(-50%) rotate(360deg); }
            }
          `}</style>
        </div>
      }
    >
      {/* Skip - New Client Button */}
      {showSkipButton && (
        <div style={{ padding: '0.5rem 0.5rem 0' }}>
          <button
            onClick={() => {
              if (onSkip) onSkip();
              onClose();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              width: '100%',
              padding: '0.875rem 1rem',
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              border: '2px dashed rgba(99, 102, 241, 0.3)',
              borderRadius: '10px',
              cursor: 'pointer',
              textAlign: isRTL ? 'right' : 'left',
              direction: isRTL ? 'rtl' : 'ltr',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <UserPlus size={20} color="#6366f1" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '0.9375rem',
                fontWeight: '600',
                color: '#6366f1',
                marginBottom: '0.125rem'
              }}>
                {t('students.newClient')}
              </div>
              <div style={{
                fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)'
              }}>
                {t('students.fillFormWithoutClient')}
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Results List */}
      {searchQuery.trim().length < 2 ? (
        <div style={{
          padding: '3rem 1.5rem',
          textAlign: 'center',
          color: 'var(--color-text-secondary)'
        }}>
          <Search size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
          <p style={{ margin: 0, fontSize: '0.9375rem' }}>
            {t('students.startTypingToSearch')}
          </p>
        </div>
      ) : searchResults.length === 0 ? (
        <div style={{
          padding: '3rem 1.5rem',
          textAlign: 'center',
          color: 'var(--color-text-secondary)'
        }}>
          <User size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
          <p style={{ margin: 0, fontSize: '0.9375rem' }}>
            {t('students.noStudents')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {searchResults.map((student) => (
            <button
              key={student.id}
              onClick={() => handleSelect(student)}
              title={`${student.firstName || ''} ${student.lastName || ''}${student.email ? ` — ${student.email}` : ''}${student.phone ? ` — ${student.phone}` : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s',
                textAlign: isRTL ? 'right' : 'left',
                width: '100%'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-primary-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <User size={20} color="var(--color-primary-dark)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '0.9375rem',
                  fontWeight: '500',
                  color: 'var(--color-text-primary)',
                  marginBottom: '0.25rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {student.firstName} {student.lastName}
                </div>
                <div style={{
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-secondary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {student.email || student.phone || t('students.noContactInfo')}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </EnterpriseModal>
  );
}
